import { z } from 'zod';
import type { StringKeyOf } from 'type-fest';

import type { Database } from '~/models/db';
import { IsoDateStringSchema } from '~/util/zod';

import { OrganizationRowSchema } from './useOrganization';
import type { OrganizationRow } from './useOrganization';

export type UserRowDb = Database['public']['Tables']['user']['Row'];

export const UserRowSchema = z.object({
  user_id: z.string().uuid(),
  created_at: IsoDateStringSchema,
  person_id: z.string().uuid().nullable(), // TODO we should make this a one-to-many join table
}) satisfies z.ZodType<UserRowDb>;

export type UserRow = z.infer<typeof UserRowSchema>;

// With joins
export type UserWithOrganizationsRowDb = UserRowDb & {
  organization: OrganizationRow[];
};

export const UserWithOrganizationsRowSchema = UserRowSchema.extend({
  organization: z.array(OrganizationRowSchema),
}) satisfies z.ZodType<UserWithOrganizationsRowDb>;

export type UserWithOrganizationsRow = z.infer<
  typeof UserWithOrganizationsRowSchema
>;
export const UserWithOrganizationsIdKey: StringKeyOf<UserRow> = 'user_id';
export type UserWithOrganizationsId = UserRow['user_id'];

/**
 * Indicates if the given item contains a valid id, likely meaning it exists in the database.
 */
export function isUserWithId(
  itemData: Partial<UserWithOrganizationsRow | UserRow>,
) {
  return (
    Object.keys(itemData).includes(UserWithOrganizationsIdKey) &&
    UserWithOrganizationsRowSchema.shape[UserWithOrganizationsIdKey].safeParse(
      itemData[UserWithOrganizationsIdKey],
    ).success
  );
}

/**
 * Gets a single user record.
 */
export default async function useUser(
  id: UserWithOrganizationsId,
): Promise<UserWithOrganizationsRow | undefined> {
  let item: UserWithOrganizationsRow | undefined;

  if (id) {
    const sb = useSupabaseClient<Database>();
    
    // First get the user record
    const userResult = await sb
      .from('user')
      .select('*')
      .eq('user_id', `${id}`)
      .maybeSingle();

    if (userResult.error) {
      throw userResult.error;
    }

    if (userResult.data) {
      // Then get the organizations through user_organization
      // Handle the case where user has no organizations gracefully
      let organizations = [];
      
      try {
        const orgResult = await sb
          .from('user_organization')
          .select('organization:organization_id(*)')
          .eq('user_id', `${id}`);

        if (orgResult.error) {
          // If it's a permission error and user has no orgs, that's ok
          if (orgResult.error.code === '42501') {
            console.log('User has no organizations yet');
          } else {
            throw orgResult.error;
          }
        } else if (orgResult.data) {
          organizations = orgResult.data.map(uo => uo.organization).filter(Boolean);
        }
      } catch (error) {
        // If we can't get organizations, continue with empty array
        console.warn('Could not fetch user organizations:', error);
      }
      
      const combinedData = {
        ...userResult.data,
        organization: organizations
      };

      item = UserWithOrganizationsRowSchema.parse(combinedData);
    }
  }

  return item;
}
